import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useTenantStore } from './stores/tenantStore';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
// TEMPORARY (2026-09-04): LandingPage import removed; root route serves LoginPage.
// To restore: uncomment the import below and swap the `/` route's element back.
// import { LandingPage } from './components/landing/LandingPage';
import { LoginPage } from './components/auth/LoginPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { AcceptInvitePage } from './components/auth/AcceptInvitePage';
import { DashboardPage } from './components/dashboard/DashboardPage';
import { StatsDetailPage } from './components/dashboard/StatsDetailPage';
import { ProjectPage } from './components/layout/ProjectPage';
import { AdminPage } from './components/admin/AdminPage';
import { ProjectListPage } from './components/projects/ProjectListPage';
import { ReferenceProjectsPage } from './components/projects/ReferenceProjectsPage';
import { ReferenceProjectDetailPage } from './components/projects/ReferenceProjectDetailPage';
import { PortfolioDashboard } from './components/ai/PortfolioDashboard';
import { OrgAnalyticsPage } from './components/analytics/OrgAnalyticsPage';
import { SettingsPage } from './components/settings/SettingsPage';
import { ComparisonPage } from './components/comparison/ComparisonPage';
import { ScenarioComparisonPage } from './components/scenarios/ScenarioComparisonPage';
import { ActivityPage } from './components/activity/ActivityPage';
import { MonteCarloPage } from './components/simulation/MonteCarloPage';
import { BenchmarkPage } from './components/benchmark/BenchmarkPage';
import { WorkflowPage } from './components/workflow/WorkflowPage';
import { StressTestPage } from './components/stress/StressTestPage';
import { ActualsPage } from './components/actuals/ActualsPage';
import { ConsultationPage } from './components/consultation/ConsultationPage';
import { PortfolioOptimizerPage } from './components/portfolio/PortfolioOptimizerPage';
import { ImportPage } from './components/import/ImportPage';
import { GlobalRatiosPage } from './components/ratios/GlobalRatiosPage';
import { RoleGuard } from './components/layout/RoleGuard';
import { getEnabledModules } from './modules';

// Lazy-load MapPage — it depends on react-leaflet/leaflet which are heavy.
// Retry with a full reload if the chunk 404s after a deployment (stale index.html).
const LazyMapPage = lazy(() =>
  import('./components/map/MapPage').then(m => ({ default: m.MapPage })).catch(() => {
    window.location.reload();
    return { default: () => null } as never;
  })
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (token) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function ModuleLoadingFallback() {
  return <div style={{ padding: 32, color: '#aaa' }}>Loading module...</div>;
}

export default function App() {
  const features = useTenantStore((s) => s.features);
  const enabledModules = getEnabledModules(features as unknown as Record<string, boolean>);

  return (
    <Routes>
      {/* TEMPORARY (2026-09-04): landing page swapped for LoginPage.
          Restore by changing this route's element back to <LandingPage />.
          Original LandingPage import + component kept intact — nothing removed. */}
      <Route path="/" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AppShell />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="stats/:metric" element={<StatsDetailPage />} />
        <Route path="projects" element={<ProjectListPage />} />
        <Route path="projects/:id" element={<ProjectPage />} />
        <Route path="projects/:id/:tab" element={<ProjectPage />} />
        <Route path="projects/:id/scenarios" element={<ScenarioComparisonPage />} />
        <Route
          path="admin"
          element={
            <RoleGuard minRole="tenant_admin">
              <AdminPage />
            </RoleGuard>
          }
        />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="map" element={<Suspense fallback={<div style={{ padding: 32, color: '#aaa' }}>Loading map...</div>}><LazyMapPage /></Suspense>} />
        <Route path="compare" element={<ComparisonPage />} />
        <Route path="simulation" element={<MonteCarloPage />} />
        <Route path="benchmark" element={<BenchmarkPage />} />
        <Route path="workflow" element={<WorkflowPage />} />
        <Route path="stress-test" element={<StressTestPage />} />
        <Route path="actuals" element={<ActualsPage />} />
        <Route path="consultation" element={<ConsultationPage />} />
        <Route path="optimizer" element={<PortfolioOptimizerPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="import" element={<ImportPage />} />
        <Route path="reference-projects" element={<ReferenceProjectsPage />} />
        <Route path="reference-projects/:id" element={<ReferenceProjectDetailPage />} />
        <Route path="analytics/:orgId" element={<OrgAnalyticsPage />} />
        <Route path="portfolio" element={<PortfolioDashboard />} />
        <Route path="ratios" element={<GlobalRatiosPage />} />

        {/* CIVITAS module routes (lazy-loaded, feature-flag-gated) */}
        {enabledModules.map((mod) =>
          mod.routes.map((route) => (
            <Route
              key={`${mod.name}-${route.path}`}
              path={route.path}
              element={
                <Suspense fallback={<ModuleLoadingFallback />}>
                  <route.component />
                </Suspense>
              }
            >
              {route.children?.map((child) => (
                <Route
                  key={`${mod.name}-${child.path}`}
                  path={child.path}
                  element={
                    <Suspense fallback={<ModuleLoadingFallback />}>
                      <child.component />
                    </Suspense>
                  }
                />
              ))}
            </Route>
          ))
        )}
      </Route>
    </Routes>
  );
}
